import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';

import { Coverage, CoverageDataResponse, CoverageMember } from './types';
import { Member } from '../employees/types';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditIcon from '@mui/icons-material/Edit';
import { EmployeeDialogs } from './index';
import formatMemberId from '../utilities/formatMemberId';
import RowIcon from '../RowIcon';

interface EmployeeCoverageProps {
  coverage: CoverageDataResponse | null;
  openDialog: (dialogType: string, { patient }: Member) => void;
  isEdi: boolean;
}

export const createCoverageMembers = (coverage: CoverageDataResponse): CoverageMember[] => {
  const showVisionStatus = Boolean(coverage.primary_coverage_vision);

  const coveragePrimary: CoverageMember = {
    dentalCoverage: coverage.primary_coverage_dental,
  };
  if (showVisionStatus) {
    coveragePrimary.visionCoverage = coverage.primary_coverage_vision;
  }
  const members: CoverageMember[] = [coveragePrimary];

  coverage?.family_coverages_dental?.forEach((value: Coverage, index: number) => {
    const coverageMember: CoverageMember = {
      dentalCoverage: value,
    };
    if (
      showVisionStatus &&
      coverage.family_coverages_vision &&
      coverage.family_coverages_vision.length > index
    ) {
      coverageMember.visionCoverage = coverage.family_coverages_vision[index];
    }
    members.push(coverageMember);
  });

  return members;
};

export default function EmployeeCoverageTable({
  coverage,
  openDialog,
  isEdi,
}: EmployeeCoverageProps) {
  if (!coverage) return;

  const showVisionStatus = Boolean(coverage.primary_coverage_vision);
  const members = createCoverageMembers(coverage);

  const NoWrap = styled(Typography)({
    display: 'inline',
    whiteSpace: 'nowrap',
    fontSize: 'inherit',
  });
  const showVisionColumns = showVisionStatus;

  const cellStyle = {
    border: '1px solid #eee',
  };

  // TODO: display dental/vision PDF dcs_url link
  return (
    <TableContainer component={Paper}>
      <Table sx={{ borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={cellStyle}>Name</TableCell>

            <TableCell sx={cellStyle}>Relation</TableCell>
            <TableCell sx={cellStyle}>Member ID</TableCell>

            <TableCell sx={cellStyle}>Dental Coverage</TableCell>

            {showVisionColumns && <TableCell sx={cellStyle}>Vision Coverage</TableCell>}
          </TableRow>
        </TableHead>

        <TableBody>
          {members.map((member, index) => (
            <TableRow key={index}>
              <TableCell sx={cellStyle}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: '100%' }}
                >
                  {member.dentalCoverage?.name}
                  <RowIcon
                    disabled={isEdi}
                    id={`vision-plan-pdf-file-${index}`}
                    icon={<EditIcon color="primary" />}
                    onClick={() => {
                      openDialog(EmployeeDialogs.EDIT_EMPLOYEE, {
                        patient: String(member.dentalCoverage?.patient_id),
                      });
                    }}
                  />
                </Stack>
              </TableCell>

              <TableCell sx={cellStyle}>{member.dentalCoverage?.relationship_label}</TableCell>
              <TableCell sx={cellStyle}>
                <NoWrap>{formatMemberId(member.dentalCoverage?.member_id)}</NoWrap>
              </TableCell>
              <TableCell sx={{ ...cellStyle, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: '100%' }}
                >
                  {member.dentalCoverage?.status_display}
                  {member.dentalCoverage?.dcs_url && (
                    <RowIcon
                      id={`dental-plan-pdf-file-${index}`}
                      icon={<PictureAsPdfIcon color="primary" />}
                      tip="View plan document"
                      onClick={() => {
                        window.open(member.dentalCoverage!.dcs_url!, '_blank');
                      }}
                    />
                  )}
                </Stack>
              </TableCell>

              {showVisionColumns && (
                <>
                  <TableCell sx={{ ...cellStyle, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ width: '100%' }}
                    >
                      {member.visionCoverage?.status_display}
                      {member.visionCoverage?.dcs_url && (
                        <RowIcon
                          id={`vision-plan-pdf-file-${index}`}
                          icon={<PictureAsPdfIcon color="primary" />}
                          tip="View plan document"
                          onClick={() => {
                            window.open(member.visionCoverage!.dcs_url!, '_blank');
                          }}
                        />
                      )}
                    </Stack>
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
